export type RoleKey =
  | "ADMIN"
  | "SUPPORT"
  | "seller"
  | "buyer"
  | "WEBMASTER"
  | "MANAGER";

export type LocaleCode = "en" | "ru" | "uk";

export type LocalizedLabel = Record<LocaleCode, string>;

export interface RoleMenuItem {
  key: string;
  route: string;
  icon: string; // lucide-react icon name
  label: LocalizedLabel;
}

export const roleMenus: Record<RoleKey, { menu: RoleMenuItem[] }> = {
  ADMIN: {
    menu: [
      {
        key: "dashboard",
        route: "/",
        icon: "LayoutDashboard",
        label: { en: "Dashboard", ru: "Дашборд", uk: "Дашборд" },
      },
      {
        key: "chats",
        route: "/chats",
        icon: "MessageCircle",
        label: { en: "My Chats", ru: "Мои чаты", uk: "Мої чати" },
      },
      {
        key: "users",
        route: "/admin/users",
        icon: "Users",
        label: { en: "Users", ru: "Пользователи", uk: "Користувачі" },
      },
      {
        key: "systemSettings",
        route: "/settings",
        icon: "Settings",
        label: {
          en: "System Settings",
          ru: "Настройки системы",
          uk: "Налаштування системи",
        },
      },
      {
        key: "analyticsDebug",
        route: "/admin/analytics-test",
        icon: "Beaker",
        label: {
          en: "Analytics Debug",
          ru: "Отладка аналитики",
          uk: "Налагодження аналітики",
        },
      },
      {
        key: "tickets",
        route: "/tickets",
        icon: "Ticket",
        label: { en: "Tickets", ru: "Тикеты", uk: "Тікети" },
      },
      // Profile intentionally removed for admin role - they don't need personal profiles
      // Not yet implemented routes intentionally omitted to avoid 404s:
      // finance
    ],
  },
  SUPPORT: {
    menu: [
      {
        key: "dashboard",
        route: "/",
        icon: "LayoutDashboard",
        label: { en: "Dashboard", ru: "Дашборд", uk: "Дашборд" },
      },
      {
        key: "chats",
        route: "/chats",
        icon: "MessageCircle",
        label: { en: "My Chats", ru: "Мои чаты", uk: "Мої чати" },
      },
      {
        key: "tickets",
        route: "/tickets",
        icon: "Ticket",
        label: { en: "Tickets", ru: "Тикеты", uk: "Тікети" },
      },
      {
        key: "analyticsDebug",
        route: "/admin/analytics-test",
        icon: "Beaker",
        label: {
          en: "Analytics Debug",
          ru: "Отладка аналитики",
          uk: "Налагодження аналітики",
        },
      },
      // Profile intentionally removed for support role - they don't need personal profiles
      // users, knowledge base, ticket reports are omitted for now
    ],
  },
  seller: {
    menu: [
      {
        key: "dashboard",
        route: "/",
        icon: "LayoutDashboard",
        label: { en: "Dashboard", ru: "Дашборд", uk: "Дашборд" },
      },
      {
        key: "chats",
        route: "/chats",
        icon: "MessageCircle",
        label: { en: "My Chats", ru: "Мои чаты", uk: "Мої чати" },
      },
      {
        key: "myOffers",
        route: "/my-offers",
        icon: "Package",
        label: { en: "My Offers", ru: "Мои офферы", uk: "Мої оффери" },
      },
      {
        key: "operators",
        route: "/contacts",
        icon: "Users",
        label: { en: "My contacts", ru: "Мои контакты", uk: "Мої контакти" },
      },
      {
        key: "tickets",
        route: "/tickets",
        icon: "Ticket",
        label: { en: "Tickets", ru: "Тикеты", uk: "Тікети" },
      },
      {
        key: "ratingsReviews",
        route: "/testimonials",
        icon: "Star",
        label: {
          en: "Ratings & Reviews",
          ru: "Рейтинг и отзывы",
          uk: "Рейтинг та відгуки",
        },
      },
      {
        key: "profile",
        route: "/profile",
        icon: "UserCircle",
        label: { en: "Profile", ru: "Профиль", uk: "Профіль" },
      },
    ],
  },
  buyer: {
    menu: [
      {
        key: "dashboard",
        route: "/",
        icon: "LayoutDashboard",
        label: { en: "Dashboard", ru: "Дашборд", uk: "Дашборд" },
      },
      {
        key: "chats",
        route: "/chats",
        icon: "MessageCircle",
        label: { en: "My Chats", ru: "Мои чаты", uk: "Мої чати" },
      },
      {
        key: "myOffers",
        route: "/my-offers",
        icon: "Package",
        label: { en: "My Offers", ru: "Мои офферы", uk: "Мої оффери" },
      },
      {
        key: "affiliates",
        route: "/contacts",
        icon: "Users",
        label: { en: "Affiliates", ru: "Аффилиаты", uk: "Аффіліати" },
      },
      {
        key: "tickets",
        route: "/tickets",
        icon: "Ticket",
        label: { en: "Tickets", ru: "Тикеты", uk: "Тікети" },
      },
      {
        key: "ratingsReviews",
        route: "/testimonials",
        icon: "Star",
        label: {
          en: "Ratings & Reviews",
          ru: "Рейтинг и отзывы",
          uk: "Рейтинг та відгуки",
        },
      },
      {
        key: "profile",
        route: "/profile",
        icon: "UserCircle",
        label: { en: "Profile", ru: "Профиль", uk: "Профіль" },
      },
    ],
  },
  WEBMASTER: {
    menu: [
      {
        key: "dashboard",
        route: "/",
        icon: "LayoutDashboard",
        label: { en: "Dashboard", ru: "Дашборд", uk: "Дашборд" },
      },
      {
        key: "news",
        route: "/news",
        icon: "Newspaper",
        label: { en: "News", ru: "Новости", uk: "Новини" },
      },
      {
        key: "chats",
        route: "/chats",
        icon: "MessageCircle",
        label: { en: "My Chats", ru: "Мои чаты", uk: "Мої чати" },
      },
      {
        key: "operators",
        route: "/contacts",
        icon: "Users",
        label: { en: "My contacts", ru: "Мои контакты", uk: "Мої контакти" },
      },
      {
        key: "tickets",
        route: "/tickets",
        icon: "Ticket",
        label: { en: "Tickets", ru: "Тикеты", uk: "Тікети" },
      },
      {
        key: "ratingsReviews",
        route: "/testimonials",
        icon: "Star",
        label: {
          en: "Ratings & Reviews",
          ru: "Рейтинг и отзывы",
          uk: "Рейтинг та відгуки",
        },
      },
      {
        key: "profile",
        route: "/profile",
        icon: "UserCircle",
        label: { en: "Profile", ru: "Профиль", uk: "Профіль" },
      },
      // my-offers путь удален для роли WEBMASTER (affiliate)
    ],
  },
  MANAGER: {
    menu: [
      {
        key: "dashboard",
        route: "/",
        icon: "LayoutDashboard",
        label: { en: "Dashboard", ru: "Дашборд", uk: "Дашборд" },
      },
      {
        key: "news",
        route: "/news",
        icon: "Newspaper",
        label: { en: "News", ru: "Новости", uk: "Новини" },
      },
      {
        key: "chats",
        route: "/chats",
        icon: "MessageCircle",
        label: { en: "My Chats", ru: "Мои чаты", uk: "Мої чати" },
      },
      {
        key: "myOffers",
        route: "/my-offers",
        icon: "Package",
        label: { en: "My Offers", ru: "Мои офферы", uk: "Мої оффери" },
      },
      {
        key: "affiliates",
        route: "/contacts",
        icon: "Users",
        label: { en: "Affiliates", ru: "Аффилиаты", uk: "Аффіліати" },
      },
      {
        key: "tickets",
        route: "/tickets",
        icon: "Ticket",
        label: { en: "Tickets", ru: "Тикеты", uk: "Тікети" },
      },
      {
        key: "ratingsReviews",
        route: "/testimonials",
        icon: "Star",
        label: {
          en: "Ratings & Reviews",
          ru: "Рейтинг и отзывы",
          uk: "Рейтинг та відгуки",
        },
      },
      {
        key: "profile",
        route: "/profile",
        icon: "UserCircle",
        label: { en: "Profile", ru: "Профиль", uk: "Профіль" },
      },
    ],
  },
};

export function pickLocale(lang: string): LocaleCode {
  const base = (lang || "en").toLowerCase();
  if (base.startsWith("ru")) return "ru";
  if (base.startsWith("uk") || base.startsWith("ua")) return "uk";
  return "en";
}
